import React, { useState, useRef, useEffect } from "react";
import { makeStyles, useTheme, Popper, Dropdown, MenuIcon } from "@seahorse/ui";
import GenericDropdown from "./CustomDropDown";
import DisplayTimeZoneDropdown from "./Co-Trader/Components/shared/DisplayTimeZoneDropdown";
import { useResetDisplayTimeZoneOnPairChange } from "./Co-Trader/Utils/displayTimeZone";

interface PopOverMenuProps {
  setCurrencyPair: (currency: string) => void;
  setUpdateSpeed: (speed: number) => void;
  setUseContext: (useContext: boolean) => void;
  currencyList: string[];
  showUpdateSpeed: boolean;
  tenorList: string[] | null;
  setTenor: any | null;
  setUserLanguage: any;
  userLanguage: string;
  currencyPair?: string;
}

const PopOverMenu: React.FunctionComponent<PopOverMenuProps> = ({
  setCurrencyPair,
  setUpdateSpeed,
  setUseContext,
  setUserLanguage,
  userLanguage,
  currencyList,
  showUpdateSpeed,
  tenorList,
  setTenor,
  currencyPair,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState("AUTO");
  useResetDisplayTimeZoneOnPairChange(currencyPair);
  const [selectedSpeed, setSelectedSpeed] = useState(3);
  const menuIconRef: any = useRef(null); // Ref to anchor the Popper
  const popperRef: any = useRef(null);
  const [selectedTenors, setSelectedTenors]: any = useState([]);
  const tenorType = tenorList?.map((item) => ({ label: item, value: item }));


  const theme = useTheme();
  const useStyles = makeStyles({
    container: {
      display: "flex",
      flexDirection: "column", // Stack the components vertically
      height: "100%",
      width: "100%",
      padding: "10px",
      backgroundColor: theme.globallink.colors.DarkGrey[500], // Background color for the combo tile
    },
    menuIcon: {
      cursor: "pointer",
      color: theme.globallink.colors.Slate[700], // Adjust icon color
    },
    popperContainer: {
      padding: "16px",
      backgroundColor: theme.globallink.colors.Slate[900], // Background for the popper
      borderRadius: "8px", // Rounded corners
      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)", // Slight shadow for depth
      maxWidth: "320px", // Limit width
      zIndex: 1000, // Match other CoTrader popovers so menus reliably sit above charts
      width: "320px",
    },
    dropdownContainer: {
      marginBottom: "12px", // Spacing between dropdowns
    },
    title: {
      color: "#E0E0E0",
      fontSize: "18px",
      marginTop: "8px",
      marginBottom: "6px",
      fontWeight: "bold",
    },
  });

  const classes = useStyles(); // Apply useStyles with theme

  const [dropDownCurrencyList, setDropDownCurrencyList] = useState([
    { label: "AUTO", value: "AUTO" },
    ...[
      { label: "USDJPY", value: "USDJPY" },
      { label: "USDKRW", value: "USDKRW" },
      { label: "EURUSD", value: "EURUSD" },
      { label: "AUDUSD", value: "AUDUSD" },
      { label: "NZDUSD", value: "NZDUSD" },
      { label: "USDCAD", value: "USDCAD" },
      { label: "USDCHF", value: "USDCHF" },
      { label: "USDNOK", value: "USDNOK" },
      { label: "USDSEK", value: "USDSEK" },
    ].sort((a, b) => a.label.localeCompare(b.label)),
  ]);
  const languageList = [
    { label: "English", value: "English" },
    { label: "Hindi", value: "Hindi" },
    { label: "Chinese", value: "Chinese" },
    { label: "Japanese", value: "Japanese" },
    { label: "Korean", value: "Korean" },
    { label: "Indonesian", value: "Indonesian" },
    { label: "Vietnamese", value: "Vietnamese" },
    { label: "Thai", value: "Thai" },
  ];

  useEffect(() => {
    if (currencyList !== null) {
      const currencyItems = [...currencyList]
        .sort((a, b) => a.localeCompare(b))
        .map((currency) => ({
          label: currency,
          value: currency,
        }));
      setDropDownCurrencyList([
        { label: "AUTO", value: "AUTO" },
        ...currencyItems,
      ]);
    }
  }, [currencyList]);
  useEffect(() => {
    const handleOutsideClick = (event: Event) => {
      setTimeout(() => {
        if (
          popperRef.current &&
          !popperRef.current.contains(event.target) &&
          menuIconRef.current &&
          !menuIconRef.current.contains(event.target)
        ) {
          setIsModalOpen(false);
        }
      }, 200); // Delay to allow dropdown interactions to resolve
    };

    document.addEventListener("mousedown", handleOutsideClick, true); // Capture phase
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick, true);
    };
  }, []);
  useEffect(() => {
  const handleStorageChange = (event: StorageEvent) => {
    if (event.key === "user_language") {
      setUserLanguage(localStorage.getItem("user_language") || "English");
    }
    if (event.key === "default_currency") {
      setSelectedCurrency(localStorage.getItem("default_currency") || "AUTO");
    }
  };

  window.addEventListener("storage", handleStorageChange);
  return () => {
    window.removeEventListener("storage", handleStorageChange);
  };
}, []);


  useEffect(() => {
    if (tenorList !== null && selectedTenors.length === 0) {
      setSelectedTenors(
        tenorList.map((item) => ({ label: item, value: item }))
      );
      setTenor(tenorList);
    }
  }, [tenorList]);

  const speedItems = [
    { label: "5s", value: 5 },
    { label: "3s", value: 3 },
    { label: "2s", value: 2 },
  ];

  const handleCurrencyChange = (value: any) => {
    if (value !== "AUTO") {
      setCurrencyPair(value);
      setUseContext(false);
    } else {
      setUseContext(true);
    }
    setSelectedCurrency(value);
  };

  const handleSpeedChange = (value: any) => {
    setSelectedSpeed(value);
    setUpdateSpeed(value);
  };
  const handleTenorChange = (value: any) => {
    setSelectedTenors(value);
    setTenor(value.map((item: any) => item.value));
  };

  const handleUserLanguageChange = (value: any) => {
    setUserLanguage(value);
    localStorage.setItem("user_language", value);
  };

  return (
    <>
      <div className={classes.container}>
        <div
          ref={menuIconRef} // This ref is used as anchorEl
          className={classes.menuIcon}
          onClick={() => setIsModalOpen((prev) => !prev)}
        >
          <MenuIcon />
        </div>

        <Popper
          open={isModalOpen}
          anchorEl={menuIconRef.current} // Set the popper just below the menu icon
          placement="bottom-start" // Ensure it appears below
          ref={popperRef}
        >
          <div className={classes.popperContainer}>
            <div className={classes.title}>Currency Pair</div>
            <div className={classes.dropdownContainer}>
              <Dropdown
                items={dropDownCurrencyList}
                value={selectedCurrency}
                onChange={handleCurrencyChange}
                allowBlankSelection={true}
                placeholder="Select currency"
                fullWidth={true}
              />
            </div>
            {showUpdateSpeed && (
              <>
                <div className={classes.title}>Update Speed</div>

                <Dropdown
                  items={speedItems}
                  value={selectedSpeed}
                  onChange={handleSpeedChange}
                  allowBlankSelection={true}
                  placeholder="Select speed"
                  fullWidth={true}
                />
              </>
            )}
            {userLanguage && (
              <>
                <div className={classes.title}>Language</div>
                <Dropdown
                  items={languageList}
                  value={userLanguage}
                  onChange={handleUserLanguageChange}
                  allowBlankSelection={true}
                  placeholder="Select language"
                  fullWidth={true}
                />
              </>
            )}
            <DisplayTimeZoneDropdown
              currencyPair={currencyPair}
              titleClassName={classes.title}
              containerClassName={classes.dropdownContainer}
            />
            {tenorType && (
              <>
                <div className={classes.title}>Tenor</div>
                <GenericDropdown
                  options={tenorType}
                  value={selectedTenors}
                  onChange={handleTenorChange}
                  isMulti={true}
                />
              </>
            )}
          </div>
        </Popper>
      </div>
    </>
  );
};

export default PopOverMenu;
